import {
	Box,
	Button,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
	BackArrowIcon,
	CloseIcon,
	EditIcon,
	PDFIcon,
	SendChatIcon,
	SendLinkIcon,
} from '../../../assets/icons/Icons';
import { GREY, MAIN_THEME_COLOR, WHITE } from '../../../assets/theme/Colors';
import ChatRow from '../../../components/ChatRow';
import CustomSwitch from '../../../components/CustomSwitch';
import { dateFormatter } from '../../../Utils/Helpers';
import {
	confirmDiscardFacebookChatDraft,
	setFacebookChatDraftDirty,
} from '../../../Utils/NavigationGuard';
import ChatNumbersList from '../../Conversations/ChatNumbersList';
import {
	ChatData,
	ChatList,
	FacebookPost,
	FacebookPostConversation,
	Tag,
} from '../../../types';

interface FacebookPostChatsViewProps {
	post: FacebookPost;
	selectedConversation: FacebookPostConversation | null;
	onSelectConversation: (conversation: FacebookPostConversation) => void;
	onUpdateBotMessage: (
		conversationId: string,
		date: string,
		messageIndex: number,
		message: string,
	) => void;
	onToggleConsultantMode: (
		conversationId: string,
		isConsultantModeOn: boolean,
	) => void;
	onSendConsultantMessage: (
		conversationId: string,
		message: ChatData,
	) => void;
	isUpdatingBotMessage?: boolean;
	isTogglingConsultantMode?: boolean;
	isSendingConsultantMessage?: boolean;
	onClose: () => void;
}

function FacebookPostChatsView({
	post,
	selectedConversation,
	onSelectConversation,
	onUpdateBotMessage,
	onToggleConsultantMode,
	onSendConsultantMessage,
	isUpdatingBotMessage = false,
	isTogglingConsultantMode = false,
	isSendingConsultantMessage = false,
	onClose,
}: FacebookPostChatsViewProps) {
	const [searchText, setSearchText] = useState('');
	const [selectedTag, setSelectedTag] = useState('All');
	const conversations = post.whatsappConversations || [];
	const chatList = useMemo(
		() => conversations.map(conversationToChatList),
		[conversations],
	);
	const filteredChatList = getFilteredChatList(
		chatList,
		selectedTag,
		searchText,
	);
	const tagList = getTagList(chatList);

	function onViewRecentChat(chat: {
		phoneNumber: string;
		name: string;
		isConsultantNeeded: boolean;
		phoneNumberExtra: string;
	}) {
		if (selectedConversation?.phoneNumber === chat.phoneNumber) {
			return;
		}

		if (!confirmDiscardFacebookChatDraft()) {
			return;
		}

		const conversation = conversations.find(
			item => item.phoneNumber === chat.phoneNumber,
		);

		if (conversation) {
			onSelectConversation(conversation);
		}
	}

	function onCloseChatsView() {
		if (confirmDiscardFacebookChatDraft()) {
			onClose();
		}
	}

	return (
		<Grid2
			container
			paddingLeft="20px"
			paddingRight="20px"
			flexDirection="column">
			<Grid2
				container
				gap="4px"
				alignItems="center"
				marginLeft="10px"
				marginBottom="10px">
				<IconButton onClick={onCloseChatsView}>
					<BackArrowIcon />
				</IconButton>
				<Typography fontSize="18px" fontWeight={700}>
					{post.name} Conversations
				</Typography>
			</Grid2>
			<Grid2
				container
				marginLeft="10px"
				columnSpacing={1}
				height="78vh"
				rowSpacing={{ xs: 1 }}>
				<ChatNumbersList
					chatListData={filteredChatList}
					onViewRecentChat={onViewRecentChat}
					selectedPhoneNumber={
						selectedConversation?.phoneNumber || ''
					}
					onSearchChatList={setSearchText}
					onChangeGridVisibility={() => {}}
					loading={false}
					tagList={tagList}
					onSelectTag={setSelectedTag}
					selectedTag={selectedTag}
					searchedText={searchText}
				/>
				<Grid2 component="div" size={{ xs: 12, md: 8 }}>
					<Grid2
						bgcolor="#FFFFFF"
						borderRadius="5px"
						sx={{ height: '100%' }}>
						{selectedConversation ? (
							<>
								<Grid2
									container
									padding="15px"
									flexDirection="column">
									<Typography
										fontSize="14px"
										fontWeight={600}
										color="#000000">
										{selectedConversation.name}
									</Typography>
									<Typography
										fontSize="12px"
										fontWeight={500}
										color="#7c7c7c">
										{selectedConversation.phoneNumber}
									</Typography>
								</Grid2>
								<ChatMessages
									key={selectedConversation.id}
									conversationId={selectedConversation.id}
									messages={selectedConversation.messages}
									isConsultantModeOn={
										selectedConversation.isConsultantNeeded
									}
									onUpdateBotMessage={onUpdateBotMessage}
									onToggleConsultantMode={
										onToggleConsultantMode
									}
									onSendConsultantMessage={
										onSendConsultantMessage
									}
									isUpdatingBotMessage={isUpdatingBotMessage}
									isTogglingConsultantMode={
										isTogglingConsultantMode
									}
									isSendingConsultantMessage={
										isSendingConsultantMessage
									}
								/>
							</>
						) : (
							<EmptyChatState />
						)}
					</Grid2>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

function ChatMessages({
	conversationId,
	messages,
	isConsultantModeOn,
	onUpdateBotMessage,
	onToggleConsultantMode,
	onSendConsultantMessage,
	isUpdatingBotMessage,
	isTogglingConsultantMode,
	isSendingConsultantMessage,
}: {
	conversationId: string;
	messages: { [index: string]: Array<ChatData> };
	isConsultantModeOn: boolean;
	onUpdateBotMessage: (
		conversationId: string,
		date: string,
		messageIndex: number,
		message: string,
	) => void;
	onToggleConsultantMode: (
		conversationId: string,
		isConsultantModeOn: boolean,
	) => void;
	onSendConsultantMessage: (
		conversationId: string,
		message: ChatData,
	) => void;
	isUpdatingBotMessage: boolean;
	isTogglingConsultantMode: boolean;
	isSendingConsultantMessage: boolean;
}) {
	const [editingMessage, setEditingMessage] = useState<{
		date: string;
		index: number;
		value: string;
	} | null>(null);
	const [message, setMessage] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const [messageError, setMessageError] = useState('');

	function onChangeConsultantMode(event: ChangeEvent<HTMLInputElement>) {
		const isEnabled = event.target.checked;

		if (!isEnabled && !confirmDiscardFacebookChatDraft()) {
			return;
		}

		onToggleConsultantMode(conversationId, isEnabled);

		if (!isEnabled) {
			setMessage('');
			onCancelSelectedFile();
		}
	}

	useEffect(() => {
		setFacebookChatDraftDirty(Boolean(message.trim() || selectedFile));

		return () => {
			setFacebookChatDraftDirty(false);
		};
	}, [message, selectedFile]);

	function onSaveEdit() {
		if (!editingMessage) {
			return;
		}

		onUpdateBotMessage(
			conversationId,
			editingMessage.date,
			editingMessage.index,
			editingMessage.value,
		);
		setEditingMessage(null);
	}

	function onSelectAttachments(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files ? event.target.files[0] : null;

		if (!file) {
			return;
		}

		if (
			!file.type.startsWith('image/') &&
			!file.type.startsWith('video/')
		) {
			setMessageError(
				'Invalid file format. Only image and video files are supported',
			);
			return;
		}

		setSelectedFile(file);
		setPreviewUrl(URL.createObjectURL(file));
		setMessageError('');

		if (event.target.value) {
			event.target.value = '';
		}
	}

	function onCancelSelectedFile() {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}

		setSelectedFile(null);
		setPreviewUrl('');
		setMessageError('');
	}

	function onSendChat() {
		if (!isConsultantModeOn || (!message.trim() && !selectedFile)) {
			return;
		}

		const currentDate = new Date();
		const todayDate = currentDate.toISOString().split('T')[0];
		const isImage = selectedFile?.type.startsWith('image/') || false;
		const isVideo = selectedFile?.type.startsWith('video/') || false;

		onSendConsultantMessage(conversationId, {
			text: selectedFile ? '' : message.trim(),
			time: currentDate.toLocaleTimeString([], {
				hour: '2-digit',
				minute: '2-digit',
			}),
			date: todayDate,
			type: 'consultant',
			fileName: selectedFile?.name,
			fileUrl: selectedFile
				? `${selectedFile.name}+${selectedFile.type}`
				: undefined,
			isFileIsImage: isImage,
			isFileIsVideo: isVideo,
			imageURL: previewUrl,
			caption: selectedFile ? message.trim() : '',
			isThisReplyMsg: false,
			repliedMsg: '',
			isMsgHasReaction: false,
			reaction: '',
		});
		setMessage('');
		setSelectedFile(null);
		setPreviewUrl('');
		setMessageError('');
		setFacebookChatDraftDirty(false);
	}

	return (
		<Grid2
			container
			flexDirection="column"
			sx={{
				height: '70vh',
				width: '100%',
			}}>
			<Box
				sx={{
					overflowY: 'auto',
					width: '100%',
					flex: 1,
					'&::-webkit-scrollbar': {
						display: 'none',
					},
					scrollbarWidth: 'none',
				}}>
				<Grid2 width="100%" padding="10px">
					{Object.keys(messages).map(date => (
						<Grid2 key={date}>
							<Typography
								variant="body2"
								fontWeight="500"
								textAlign="center">
								{dateFormatter(date)}
							</Typography>
							{messages[date].map((chat, index) => {
								const isBotMessage =
									chat.type === 'bot' || chat.type === 'ai';
								const isEditing =
									editingMessage?.date === date &&
									editingMessage.index === index;

								return (
									<Grid2 key={`${date}-${index}`}>
										{isEditing ? (
											<EditMessageRow
												value={editingMessage.value}
												isSaving={isUpdatingBotMessage}
												onChange={value =>
													setEditingMessage({
														...editingMessage,
														value,
													})
												}
												onCancel={() =>
													setEditingMessage(null)
												}
												onSave={onSaveEdit}
											/>
										) : (
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													width: '100%',
												}}>
												<Box sx={{ width: '100%' }}>
													<ChatRow
														position={
															chat.type ===
															'human'
																? 'left'
																: 'right'
														}
														text={chat.text}
														chatTime={chat.time}
														fileName={chat.fileName}
														fileUrl={chat.fileUrl}
														caption={chat.caption}
														isFileIsImage={
															chat.isFileIsImage
														}
														isFileIsVideo={
															chat.isFileIsVideo
														}
														imageURL={chat.imageURL}
														chatOwner={
															chat.type === 'bot'
																? 'ai'
																: chat.type
														}
													/>
												</Box>
												{isBotMessage && (
													<Box
														sx={{
															display: 'flex',
															justifyContent:
																'flex-end',
															alignItems:
																'center',
															gap: '4px',
															marginLeft: 'auto',
															marginTop: '-8px',
															maxWidth: {
																xs: '90%',
																sm: '70%',
															},
															pr: {
																xs: '12px',
																sm: '48px',
															},
															pb: '6px',
														}}>
														<IconButton
															sx={{
																color: MAIN_THEME_COLOR,
																height: '28px',
																width: '28px',
																padding: '4px',
															}}
															disabled={
																isUpdatingBotMessage
															}
															onClick={() =>
																setEditingMessage(
																	{
																		date,
																		index,
																		value: chat.text,
																	},
																)
															}>
															<EditIcon />
														</IconButton>
													</Box>
												)}
											</Box>
										)}
									</Grid2>
								);
							})}
						</Grid2>
					))}
				</Grid2>
			</Box>
			<ChatComposer
				message={message}
				selectedFile={selectedFile}
				previewUrl={previewUrl}
				messageError={messageError}
				isConsultantModeOn={isConsultantModeOn}
				onChangeMessage={setMessage}
				onSelectAttachments={onSelectAttachments}
				onCancelSelectedFile={onCancelSelectedFile}
				onSendChat={onSendChat}
				onChangeConsultantMode={onChangeConsultantMode}
				isTogglingConsultantMode={isTogglingConsultantMode}
				isSendingConsultantMessage={isSendingConsultantMessage}
			/>
		</Grid2>
	);
}

function EditMessageRow({
	value,
	isSaving,
	onChange,
	onCancel,
	onSave,
}: {
	value: string;
	isSaving: boolean;
	onChange: (value: string) => void;
	onCancel: () => void;
	onSave: () => void;
}) {
	return (
		<Grid2 container justifyContent="flex-end" padding="10px">
			<Box
				sx={{
					width: { xs: '100%', md: '65%' },
					maxWidth: { xs: '100%', md: '65%' },
				}}>
				<TextField
					value={value}
					onChange={event => onChange(event.target.value)}
					multiline
					minRows={2}
					fullWidth
				/>
				<Grid2
					container
					justifyContent="flex-end"
					alignItems="center"
					columnGap={1}
					marginTop="8px">
					<Button
						sx={{
							color: MAIN_THEME_COLOR,
							textTransform: 'none',
							fontWeight: 600,
						}}
						disabled={isSaving}
						onClick={onCancel}>
						Cancel
					</Button>
					<Button
						sx={{
							backgroundColor: MAIN_THEME_COLOR,
							color: '#FFFFFF',
							textTransform: 'none',
							fontWeight: 600,
							'&:hover': {
								backgroundColor: '#1F5E62',
							},
						}}
						disabled={isSaving}
						onClick={onSave}>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
				</Grid2>
			</Box>
		</Grid2>
	);
}

function ChatComposer({
	message,
	selectedFile,
	previewUrl,
	messageError,
	isConsultantModeOn,
	onChangeMessage,
	onSelectAttachments,
	onCancelSelectedFile,
	onSendChat,
	onChangeConsultantMode,
	isTogglingConsultantMode,
	isSendingConsultantMessage,
}: {
	message: string;
	selectedFile: File | null;
	previewUrl: string;
	messageError: string;
	isConsultantModeOn: boolean;
	onChangeMessage: (message: string) => void;
	onSelectAttachments: (event: ChangeEvent<HTMLInputElement>) => void;
	onCancelSelectedFile: () => void;
	onSendChat: () => void;
	onChangeConsultantMode: (event: ChangeEvent<HTMLInputElement>) => void;
	isTogglingConsultantMode: boolean;
	isSendingConsultantMessage: boolean;
}) {
	const isFileSelected = Boolean(selectedFile);

	return (
		<Grid2 padding="10px" borderTop="1px solid #EEF2F3">
			{messageError && (
				<Typography color="error" fontSize="12px" marginBottom="6px">
					{messageError}
				</Typography>
			)}
			{isConsultantModeOn && selectedFile && (
				<AttachmentPreview
					file={selectedFile}
					previewUrl={previewUrl}
					onCancel={onCancelSelectedFile}
				/>
			)}
			<Grid2 container spacing={1.5} alignItems="flex-end">
				{isConsultantModeOn ? (
					<Grid2
						component="div"
						size={{ xs: 12, md: 9 }}
						sx={{
							display: 'flex',
							alignItems: 'center',
							marginY: isFileSelected ? '0px' : '5px',
							padding: '5px',
							bgcolor: '#F2F4F5',
							borderTopLeftRadius: isFileSelected
								? '0px'
								: '20px',
							borderTopRightRadius: isFileSelected
								? '0px'
								: '20px',
							borderBottomLeftRadius: '20px',
							borderBottomRightRadius: '20px',
						}}>
						<TextField
							type="text"
							variant="standard"
							multiline
							placeholder="Type your message here..."
							sx={{
								width: { md: '93%', xs: '82%' },
								'& fieldset': {
									border: 'none',
								},
							}}
							slotProps={{
								input: {
									style: {
										color: '#000000',
										paddingLeft: '5px',
										fontSize: '14px',
									},
									disableUnderline: true,
								},
							}}
							value={message}
							onChange={event =>
								onChangeMessage(event.target.value)
							}
							onKeyDown={event => {
								const isMobile =
									'ontouchstart' in window ||
									/Mobi|Android/i.test(navigator.userAgent);

								if (
									event.key === 'Enter' &&
									!event.shiftKey &&
									!isMobile
								) {
									event.preventDefault();
									onSendChat();
								}
							}}
						/>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignSelf: 'flex-end',
								alignItems: 'center',
								marginLeft: '8px',
							}}>
							<IconButton component="label">
								<SendLinkIcon />
								<input
									hidden
									type="file"
									accept="image/*,video/*"
									disabled={isSendingConsultantMessage}
									onChange={onSelectAttachments}
								/>
							</IconButton>
							<IconButton
								disabled={
									isSendingConsultantMessage ||
									(!isFileSelected && !message.trim())
								}
								onClick={onSendChat}>
								<SendChatIcon />
							</IconButton>
						</Box>
					</Grid2>
				) : (
					<Grid2
						component="div"
						size={{ xs: 12, md: 9 }}
						sx={{
							display: 'flex',
							alignItems: 'center',
							marginY: '5px',
							padding: '5px',
							bgcolor: '#FFFFFF',
						}}>
						<Typography color="#7C7C7C" fontSize="13px">
							Bot mode is active. Enable consultant mode to send
							manual replies.
						</Typography>
					</Grid2>
				)}
				<Grid2
					component="div"
					size={{ xs: 7, md: 3 }}
					sx={{
						border: `1px solid ${isConsultantModeOn ? MAIN_THEME_COLOR : GREY}`,
						borderRadius: '5px',
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						paddingLeft: '4px',
						marginY: '5px',
						height: 45,
						backgroundColor: isConsultantModeOn ? '#DEECED' : WHITE,
						ml: 'auto',
					}}>
					<Typography
						fontSize="14px"
						color={isConsultantModeOn ? MAIN_THEME_COLOR : GREY}
						sx={{ marginRight: '5px' }}>
						Consultant Mode
					</Typography>
					<CustomSwitch
						isChecked={isConsultantModeOn}
						handleChange={onChangeConsultantMode}
						disabled={isTogglingConsultantMode}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

function AttachmentPreview({
	file,
	previewUrl,
	onCancel,
}: {
	file: File;
	previewUrl: string;
	onCancel: () => void;
}) {
	const isImage = file.type.startsWith('image/');
	const isVideo = file.type.startsWith('video/');

	return (
		<Box
			sx={{
				position: 'relative',
				backgroundColor: '#F2F4F5',
				width: { md: '74.5%', xs: '100%' },
				borderTopLeftRadius: '20px',
				borderTopRightRadius: '20px',
				padding: '10px',
			}}>
			<Grid2
				component="div"
				sx={{
					width: '90%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
				}}>
				{isImage && (
					<img
						src={previewUrl}
						alt="Preview"
						style={{
							width: '95%',
							maxHeight: '120px',
							objectFit: 'contain',
						}}
					/>
				)}
				{isVideo && (
					<video
						src={previewUrl}
						controls
						style={{
							width: '95%',
							maxHeight: '120px',
							objectFit: 'contain',
						}}
					/>
				)}
				{!isImage && !isVideo && <PDFIcon />}
				<Typography
					color="#292D32"
					fontSize="12px"
					sx={{ wordBreak: 'break-word' }}>
					{file.name}
				</Typography>
				<Typography color="#A9ACB4" fontSize="10px">
					{(file.size / 1024).toFixed(2)} KB
				</Typography>
			</Grid2>
			<IconButton
				sx={{
					position: 'absolute',
					top: '20px',
					right: '20px',
					padding: '0px',
				}}
				onClick={onCancel}>
				<CloseIcon />
			</IconButton>
		</Box>
	);
}

function EmptyChatState() {
	return (
		<Grid2
			container
			width="100%"
			height="70vh"
			justifyContent="center"
			alignItems="center">
			<Typography fontSize="16px" fontWeight="400">
				No chat data available
			</Typography>
		</Grid2>
	);
}

function conversationToChatList(
	conversation: FacebookPostConversation,
): ChatList {
	return {
		name: conversation.name,
		phoneNumber: conversation.phoneNumber,
		timestamp: conversation.timestamp,
		isConsultantNeeded: conversation.isConsultantNeeded,
		unreadMessageCount: conversation.unreadMessageCount,
	};
}

function getTagList(chatList: Array<ChatList>): Array<Tag> {
	return [
		{
			name: 'All',
			count: chatList.length,
		},
		{
			name: 'Escalated',
			count: chatList.filter(item => item.isConsultantNeeded).length,
		},
		{
			name: 'Unread',
			count: chatList.filter(item => item.unreadMessageCount !== 0)
				.length,
		},
	];
}

function getFilteredChatList(
	chatList: Array<ChatList>,
	selectedTag: string,
	searchText: string,
) {
	const filteredByTag =
		selectedTag === 'Escalated'
			? chatList.filter(item => item.isConsultantNeeded)
			: selectedTag === 'Unread'
				? chatList.filter(item => item.unreadMessageCount !== 0)
				: chatList;

	if (searchText === '') {
		return filteredByTag;
	}

	const normalizedSearchText = searchText.toLowerCase();

	return filteredByTag.filter(
		item =>
			item.name.toLowerCase().includes(normalizedSearchText) ||
			item.phoneNumber.toLowerCase().includes(normalizedSearchText),
	);
}

export default FacebookPostChatsView;
